// Login — one door, one method (2026-08-29).
//
// SMS text-code sign-in is RETIRED as a login method (recycled-number /
// SIM-swap exposure; every first booking now creates a full
// email+password account). The phone number remains the identity/merge
// anchor, verification proof, and transactional channel — it just
// isn't a session key anymore. Password recovery stays on
// /forgot-password.
//
// The client/provider role toggle is gone with it: the toggle only
// existed to keep SMS copy away from providers. Email+password is the
// same form for everyone; the server routes by accountType after
// login. "Create an account" forks: clients are provider-anchored
// (find your therapist → /p/:code/join), providers go to /signup.
import React, { useContext, useRef, useState } from 'react';
import { logoSrc } from '../utils/logoSrc';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import axios from 'axios';
import {
  AlertCircle, ArrowRight, ChevronRight, Eye, EyeOff, Loader2,
} from 'lucide-react';
import { BrushStroke } from './brush/BrushMotifs';
import PhoneSigninModal from './PhoneSigninModal';

const SUBCOPY = {
  signin: 'Sign in to tend to your appointments.',
  create: 'Find your therapist — your account is created on their page.',
};

const inputClass = `block w-full px-3.5 py-3.5 bg-paper-elev border border-line rounded-btn
  text-[15px] text-ink placeholder-ink-3
  focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent
  transition-all`;

const Login = () => {
  const [pane, setPane] = useState('signin');
  // Text-code sign-in door (Ivan, 2026-09-01): concierge bookings may
  // create SMS-only accounts, so the code path needs a visible door.
  const [smsOpen, setSmsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useContext(AuthContext);
  const [clientCode, setClientCode] = useState('');
  const [providerHits, setProviderHits] = useState([]);
  const searchTimer = useRef(null);

  const switchPane = (next) => {
    setError('');
    setPane(next);
  };

  // Post-login routing — same registrationStep / accountType branching
  // as always.
  const routeAfterLogin = (user) => {
    setUser(user);
    const registrationStep = user.registrationStep || 1;
    // isAdmin no longer redirects here — /admin is reached by URL
    // (2026-08-30); admins land wherever their accountType lands.
    // profile-setup is PROVIDER onboarding only (2026-08-29) — client
    // doors capture everything at creation; booking-minted accounts
    // carry registrationStep 1 and must never see the legacy funnel.
    if (registrationStep === 1 && user.accountType === 'PROVIDER') navigate('/profile-setup');
    // registrationStep 2 (treatment prefs) retired — legacy accounts go home.
    else navigate(user.accountType === 'PROVIDER' ? '/provider' : '/');
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const response = await axios.post('/api/auth/login',
        { email, password },
        { withCredentials: true, headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
      );
      if (response.data.user) routeAfterLogin(response.data.user);
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const searchProviders = (q) => {
    setClientCode(q);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const query = q.trim();
    if (query.length < 2) { setProviderHits([]); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/join-code/search?q=${encodeURIComponent(query)}`, { credentials: 'include' });
        const data = res.ok ? await res.json() : null;
        setProviderHits(data?.providers || []);
      } catch { setProviderHits([]); }
    }, 250);
  };

  const goToProviderPage = async (e) => {
    e.preventDefault();
    const codeStr = clientCode.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    if (!codeStr) return;
    setError('');
    setIsLoading(true);
    try {
      const res = await fetch(`/api/join-code/verify/${codeStr}`, { credentials: 'include' });
      if (!res.ok) throw new Error('not found');
      navigate(`/p/${codeStr}/join`);
    } catch {
      setError("We couldn't find that page — double-check the name with your therapist.");
    } finally {
      setIsLoading(false);
    }
  };

  const errorBox = error && (
    <div className="flex items-start gap-2 p-3 border border-[rgba(192,106,62,0.30)] bg-[var(--r-off-soft)] rounded-btn">
      <AlertCircle className="h-5 w-5 text-[var(--r-off)] flex-shrink-0 mt-0.5" />
      <p className="text-sm text-[var(--r-off-ink)]">{error}</p>
    </div>
  );

  const primaryBtnClass = `w-full inline-flex items-center justify-center gap-2 py-3.5 px-5
    rounded-btn text-[15px] font-medium text-white bg-accent
    hover:bg-accent-ink transition
    disabled:opacity-50 disabled:cursor-not-allowed`;

  const orDivider = (
    <div className="flex items-center gap-3 my-1.5" style={{ gap: 12 }}>
      <span className="flex-1 h-px bg-line" />
      <span className="text-[11px] uppercase tracking-[0.12em] text-ink-3">or</span>
      <span className="flex-1 h-px bg-line" />
    </div>
  );

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <div className="max-w-md mx-auto w-full px-7 pt-10 pb-8">
        {/* Avayble icon */}
        <div className="flex justify-center mb-3">
          <img src="/imgs/avayble_icon.png" alt="" className="h-16 w-16" />
        </div>

        {/* Eyebrow + headline */}
        <div className="text-center mb-10">
          <div className="av-meta mb-1.5">Avayble · est. 2026</div>
          <h1 className="font-display text-[40px] leading-[1.05] tracking-tight m-0 mb-2 text-ink">
            A quiet hour,<br />
            <em className="not-italic" style={{ color: 'var(--accent)', fontStyle: 'italic' }}>on your schedule.</em>
          </h1>
          {/* Subcopy — reserved height so panes don't shift the page. */}
          <p className="text-sm text-ink-2 m-0" style={{ minHeight: '1.4em' }}>{SUBCOPY[pane]}</p>
        </div>

        {/* ── Pane: Sign in — the one method, for everyone ── */}
        {pane === 'signin' && (
          <form onSubmit={handleEmailSubmit} className="flex flex-col gap-3.5">
            <div>
              <label htmlFor="email" className="av-meta block mb-1.5">Email</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="password" className="av-meta block mb-1.5">Password</label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className={`${inputClass} pr-12`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-ink-3 hover:text-ink-2 transition"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            {errorBox}
            <button
              type="submit"
              disabled={isLoading}
              className={`mt-2 ${primaryBtnClass}`}
              style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.08), 0 0 0 0 rgba(176,122,78,0.22)' }}
            >
              {isLoading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Entering...</>
              ) : (
                <>Enter <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
            <div className="text-center text-[13px] text-ink-3 -mt-1 flex items-center justify-center gap-3">
              <Link to="/forgot-password" className="hover:text-ink-2 transition">Forgot password?</Link>
              <span aria-hidden="true">·</span>
              <button
                type="button"
                onClick={() => setSmsOpen(true)}
                className="hover:text-ink-2 transition"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit', color: 'inherit' }}
              >
                Sign in with a text code
              </button>
            </div>
            <PhoneSigninModal open={smsOpen} onClose={() => setSmsOpen(false)} />
            {orDivider}
            <button
              type="button"
              onClick={() => switchPane('create')}
              className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-5
                rounded-btn text-[15px] font-medium text-accent bg-paper-elev
                border border-accent hover:bg-accent-soft transition"
              style={{ cursor: 'pointer' }}
            >
              Create an account
            </button>
          </form>
        )}

        {/* ── Pane: Create — clients are provider-anchored (find the
            therapist, account is created on their page); providers get
            the /signup link below. ── */}
        {pane === 'create' && (
          <form onSubmit={goToProviderPage} className="flex flex-col gap-3.5">
            <div>
              <label htmlFor="clientCode" className="av-meta block" style={{ marginBottom: 6 }}>Find your therapist</label>
              <input
                id="clientCode"
                type="text"
                autoComplete="off"
                value={clientCode}
                onChange={(e) => searchProviders(e.target.value)}
                placeholder="Therapist or business name…"
                className={inputClass}
              />
              {providerHits.length > 0 && (
                <div
                  className="bg-paper-elev border border-line"
                  style={{ borderRadius: 12, marginTop: 8, overflow: 'hidden' }}
                >
                  {providerHits.map(h => (
                    <button
                      key={h.joinCode}
                      type="button"
                      onClick={() => navigate(`/p/${h.joinCode}/join`)}
                      className="w-full flex items-center text-left hover:bg-accent-soft transition"
                      style={{ gap: 12, padding: '11px 14px', background: 'none', border: 0, borderBottom: '1px solid var(--line-2)', cursor: 'pointer' }}
                    >
                      {h.logoUrl ? (
                        <img src={logoSrc(h.logoUrl, 32)} alt="" style={{ width: 32, height: 32, borderRadius: 999, objectFit: 'contain', background: 'var(--bg-elev)', border: '1px solid var(--line)', flexShrink: 0 }} />
                      ) : (
                        <span className="flex items-center justify-center flex-shrink-0 text-[13px] font-medium" style={{ width: 32, height: 32, borderRadius: 999, background: 'var(--accent-soft)', color: 'var(--accent-ink)' }}>
                          {(h.businessName || h.providerName || '?').slice(0, 1)}
                        </span>
                      )}
                      <span className="flex-1 min-w-0">
                        <span className="block text-[14px] font-medium text-ink truncate">{h.businessName || h.providerName}</span>
                        {h.businessName && h.providerName && (
                          <span className="block text-[12px] text-ink-2 truncate">{h.providerName}</span>
                        )}
                      </span>
                      <ChevronRight className="flex-shrink-0" style={{ width: 16, height: 16, color: 'var(--ink-3)' }} />
                    </button>
                  ))}
                </div>
              )}
            </div>
            {errorBox}
            <button type="submit" disabled={isLoading || !clientCode.trim()} className={primaryBtnClass}>
              {isLoading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Finding the page...</>
              ) : (
                <>Create my account <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
            <p className="text-[0.8rem] text-ink-2 m-0 text-center" style={{ lineHeight: 1.5 }}>
              A minute of setup on your therapist's page — no booking required.
              Their page name (avayble.app/p/…) works here too.
            </p>
            <p className="text-[0.8rem] text-ink-2 m-0 text-center">
              A provider?{' '}
              <Link to="/signup" className="underline" style={{ color: 'var(--accent-ink)' }}>
                Create a provider account →
              </Link>
            </p>
            <button
              type="button"
              onClick={() => switchPane('signin')}
              className="block mx-auto text-[13px] text-ink-3 hover:text-ink-2 transition"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            >
              ← Back to sign in
            </button>
          </form>
        )}
      </div>

      {/* Brush accent at bottom */}
      <div className="flex justify-center pb-8">
        <BrushStroke width={120} height={12} color="var(--accent)" opacity={0.5} />
      </div>
    </div>
  );
};

export default Login;
